type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null;

// Nested changeset params arrive joined with "/" (e.g. "address/postalCode");
// only the leaf is meaningful to the user.
const getParameterLeaf = (parameter: string) => parameter.split('/').pop() ?? parameter;

const humanizeParameter = (parameter: string) =>
  getParameterLeaf(parameter)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/^./, (character) => character.toUpperCase());

const E164_PHONE_NUMBER_EXAMPLE = '18005551234';

const appendParameterExample = (message: string, parameterName: string) => {
  const normalizedParameter = getParameterLeaf(parameterName).replace(/[_-]+/g, '').toLowerCase();
  const isPhoneNumber = ['phonenumber', 'mobilephonenumber'].includes(normalizedParameter);

  if (!isPhoneNumber || !/\bE\.?164\b/i.test(message)) return message;

  return `${message.replace(/\.+$/, '')}. Example: ${E164_PHONE_NUMBER_EXAMPLE}.`;
};

const PERMISSION_DENIED_MESSAGE = "You don't have permission to make this change.";
const NOT_FOUND_MESSAGE = 'This record no longer exists. Refresh the page and try again.';

// Mercury error codes (Mercury.GQL.SchemaUtils) mapped to user-facing copy.
// Server messages for these codes are terse or caller-supplied, so they are
// never shown directly.
const CODE_MESSAGES: Record<string, string> = {
  unauthorized: 'Your session has expired. Please log in again.',
  forbidden: PERMISSION_DENIED_MESSAGE,
  'forbidden.no_self_delete': "You can't delete your own account or its parent account.",
  not_found: NOT_FOUND_MESSAGE,
  resource_not_found: NOT_FOUND_MESSAGE,
  conflict: 'This change conflicts with an existing record.',
  service_unavailable: 'The service is temporarily unavailable. Please try again shortly.',
};

const getCode = (error: UnknownRecord) => {
  if (typeof error.code === 'string') return error.code;

  const extensions = error.extensions;
  if (isRecord(extensions) && typeof extensions.code === 'string') return extensions.code;
};

const getParameterName = (error: UnknownRecord) => {
  const parameter = error.parameter;
  if (isRecord(parameter) && typeof parameter.name === 'string') return parameter.name;

  const extensions = error.extensions;
  if (!isRecord(extensions)) return;

  const extensionParameter = extensions.parameter;
  if (isRecord(extensionParameter) && typeof extensionParameter.name === 'string') {
    return extensionParameter.name;
  }
};

const formatParameterError = (error: UnknownRecord) => {
  if (typeof error.message !== 'string' || !error.message.trim()) return;

  const message = error.message.trim();
  const parameterName = getParameterName(error);
  if (!parameterName) return message;

  const parameter = humanizeParameter(parameterName);
  let formattedMessage = message;

  if (message.toLowerCase().includes(parameter.toLowerCase())) {
    formattedMessage = message;
  } else if (
    /^(is|isn't|are|aren't|does|doesn't|must|mustn't|should|shouldn't|cannot|can't|was|wasn't|has|hasn't)\b/i.test(
      message
    )
  ) {
    formattedMessage = `${parameter} ${message}`;
  } else {
    formattedMessage = `${parameter}: ${message}`;
  }

  return appendParameterExample(formattedMessage, parameterName);
};

const formatError = (error: unknown) => {
  if (!isRecord(error)) return;

  const code = getCode(error);
  if (!code) return;
  if (code === 'parameter_error') return formatParameterError(error);
  if (code.startsWith('access_denied.')) return PERMISSION_DENIED_MESSAGE;

  return CODE_MESSAGES[code];
};

/**
 * Returns user-facing messages for known Mercury GraphQL error codes.
 * `parameter_error` validation messages are shown with their field name; other
 * known codes map to fixed copy. Unknown codes, code-less GraphQL errors and
 * transport errors return undefined so callers can use a generic fallback
 * instead of exposing implementation details.
 */
export const getGraphQLErrorMessage = (error: unknown): string | undefined => {
  if (!isRecord(error) || !Array.isArray(error.errors)) return;

  const messages = error.errors
    .map(formatError)
    .filter((message): message is string => !!message)
    // Ecto validation messages have no trailing punctuation.
    .map((message) => (/[.!?]$/.test(message) ? message : `${message}.`));
  return messages.length > 0 ? [...new Set(messages)].join(' ') : undefined;
};
